<template>
  <div
      ref="capture"
      :id="isFakeRendering ? 'main_html'+renderingOrder:'main_html'"
      :style="getHTMLStyle"
      class="html-container canvas-main-html-container"
      @dblclick.stop="onCanvasDoubleClick($event)"
      @click.stop="onCanvasClick"
  >
    <component
        v-for="(object,index) in getContent"
        :is="object.type+'-object'"
        :element-index="index"
        :data-pageindex = "object.PageIndex"
        :key="object.Id"
        :data="object"
        :object-props="object"
        :scale="zoom"
        :is-fake-rendering="isFakeRendering"
        :display-controls="true"
    />


  </div>
</template>
<script>
/* eslint-disable max-len */
import {
  Text, Image as ImageObj, Path, Video, Table, Group
} from '@/components/CanvasShapes';
import EventBus from '@/EventBus';
import canvasTextFunctions from '@/components/Canvas/mixins/canvasTextFunctions';
import canvasSelectionManager from '@/components/Canvas/mixins/canvasSelectionManager';
import ApplicationEventTypes from '@/mixins/ApplicationEventTypes'; // TDOD: Change to @/
import canvasImageFunctions from '@/components/Canvas/mixins/canvasImageFunctions';
import canvasShapeFunctions from '@/components/Canvas/mixins/canvasShapeFunctions';
import _ from 'lodash';
import { uuid } from 'vue-uuid';
import canvasVideoFunctions from '@/components/Canvas/mixins/canvasVideoFunctions';
import GroupObject from '@/components/DesignObjects/GroupObject';
import {
  canGroupObjects,
  flattenGroup,
  reassignGroupIds,
  findObjectById,
  findParentGroup,
  recalculateGroupBounds,
  addLayersToExistingGroup,
  replaceLayersWithGroup,
  getUniqueGroupName,
  resolveTopLevelSelection, getNextTableName,
  getPropertyApplyTargets,
  filterSameTypeAnimationTargets,
} from '@/components/Canvas/utils/groupHelpers';
import canvasContextMenu from '@/components/Canvas/mixins/canvasContextMenu';
import TextCurveMixin from '../../mixins/TextCurveMixin';

export default {

  name:       'Canvas',
  components: {
    'text-object':  Text,
    'shape-object': Path,
    'image-object': ImageObj,
    'video-object': Video,
    'group-object': Group,
    'table-object': Table,
  },
  mixins: [canvasTextFunctions, canvasSelectionManager, canvasImageFunctions, canvasShapeFunctions, canvasVideoFunctions, TextCurveMixin, canvasContextMenu],
  props:  {
    uniqueId: {
      type:    String,
      default: '-1',
    },
    width:              Number,
    height:             Number,
    scale:              Number,
    activePage:         Number,
    contentList:        { Object, Array },
    baseImage:          String,
    productCrop:        { Object },
    productScale:       Array,
    isGrayScaleOn:      Boolean,
    isAlignmentGuideOn: { type: Boolean, default: false },
    isFakeRendering:    Boolean,
    renderingOrder:     Number,

    selectedObjectsList: {
      type:    Array,
      default: () => [],
    },
    product: {
      type:    Object,
      default: () => {},
    },
    verticalGuides: {
      type:    Array,
      default: () => [],
    },
    horizontalGuides: {
      type:    Array,
      default: () => [],
    },
  },
  data() {
    return {
      isObjectSelected:           false,
      container:                  null,
      moveableObj:                { target: null },
      handlerTriggeredCount:      0,
      selectO:                    null,
      zoom:                       1,
      targets:                    [],
      activeObject:               null,
      activeEditor:               null,
      objectList:                 this.contentList,
      isMounted:                  false, // used in canvas panel
      isProductChangedByUndoRedo: false, // used in undo redo manager
      allTextEditorList:          {},
      calcRatio:                  0.3,
      selectionAnchorId:          null,
    };
  },
  computed: {
    getIsAlignmentGuideOn() {
      return this.isAlignmentGuideOn;
    },
    getScale() {
      return `scale( ${this.zoom})`;
    },
    getWidth() {
      return `${this.width}px`;
    },
    getHeight() {
      return `${this.height}px`;
    },
    getContent() {
      return this.objectList;
    },
    getHtmlStringFromActiveObject() {
      return this.activeObject ? this.activeObject.Value : null;
    },
    geteditorFromContentList(index) {
      return this.objectList[index].editor;
    },
    getTranslate() {
      const { AllowCropping, XCrop, YCrop } = this.productCrop;
      return AllowCropping ? `translate(${-parseFloat(XCrop)}px,${-parseFloat(YCrop)}px)` : '';
    },
    getElementWrapperStyle() {
      if (!this.productScale || !this.productScale[0] || !this.productScale[1]) {
        return 'scale(1,1)';
      }
      return {
        transform:       `scale(${this.productScale[0]},${this.productScale[1]})`,
        transformOrigin: 'top left',
      };
    },
    getHTMLStyle() {
      // ${this.getTranslate}
      const {
        AllowCropping, XCrop, YCrop, WidthCrop, HeightCrop,
      } = this.productCrop;
      const scaleRatio = this.scale / 100;
      const transform  = `scale(${scaleRatio}) `;
      const newStyle   = {
        transform,
        width:            `${this.width}px`,
        height:           `${this.height}px`,
        backgroundColor:  '#ffffff',
        overflow:         (this.activeObject && this.activeObject.isEditorEnable) ? '' : 'hidden',
        // pageBreakAfter:  'always',
        backgroundImage:  `url(${this.baseImage})`,
        backgroundRepeat: 'no-repeat',
        filter:           this.isGrayScaleOn ? 'grayscale(1)' : 'grayscale(0)',
        marginLeft:       AllowCropping ? `${-parseFloat(XCrop * scaleRatio)}px` : '0px',
        marginTop:        AllowCropping ? `${-parseFloat(YCrop * scaleRatio)}px` : '0px',
        backgroundSize:   `${AllowCropping ? this.width + parseFloat(XCrop) : this.width}px ${AllowCropping ? this.height + parseFloat(YCrop) : this.height}px`,
      };
      return newStyle;
    },
  },
  watch: {
    getContent: {
      deep: true,
      handler() {
        this.updateElementSnapping();
      },
    },
    activeObject: {
      handler(newObject) {
        this.updateMoveableTargets(newObject);
      },
    },
    targets2: {
      handler(newTargets) {
        const { target } =  this.moveableObj;

        if (target && target.length > 0) {
          const object =   this.objectList[parseInt(target[0].dataset.elementindex, 10)];
          object && object.type === 'text' && (object.Value = object.modifiedHtml);
        }
        if (this.isTextEditModeActive && this.isTextEditModeActive()) {
          this.hideMoveableDuringTextEdit();
          return;
        }
        if (!this.hasTransformableSelection()) {
          this.syncMoveableForLockState();
          return;
        }
        this.moveableObj.target = [];
        if (newTargets.length === 0) {
          this.moveableObj.target = newTargets;
        } else {
          const object = this.objectList[parseInt(newTargets[0].dataset.elementindex, 10)];
          if (object && object.IsVisible && !object.locked) {
            this.moveableObj.target = newTargets;
          }
        }
        if (newTargets && newTargets.length > 0 && newTargets[0].dataset.type === 'text') {
          setTimeout(() => {
            this.getTextSelectionProperties();
          });
        }
        newTargets.forEach((element) => {
          this.objectList.forEach((object) => {
            if (object.Id === element.id && object.IsVisible) {
              object.isSelected = true;
            }
          });
          this.setActiveObject();
        });
      },
    },
  },
  updated() {
    // document.querySelector('.rCS1w3zcxh  .moveable-area.moveable-pass').style.pointerEvents = 'all';
    this.$nextTick().then(() => {

    });
  },
  mounted() {
    if (!this.isFakeRendering) {
      this.$nextTick(this.bindClickEventToMoveableControls);
      this.isMounted = true;
      this.container = document.querySelector('#canvas-wrapper');
      this.initSelectManager();
      this.initMoveableManager();
      this.initContextMenu();
      window.addEventListener('keydown', this.doCommand);
      window.addEventListener('resize', this.updateOnWindowResize);
      document.addEventListener('dblclick', this.onDocumentDoubleClick, true);
      window.onresize =    this.moveableObj.updateRect();

      EventBus.$on(ApplicationEventTypes.ObjectSelectionEvents.OBJECT_DELETED, this.removeObjectFromCanvas);
      EventBus.$on(ApplicationEventTypes.ObjectSelectionEvents.UPDATED, this.onObjectUpdated);
      EventBus.$on(ApplicationEventTypes.ObjectSelectionEvents.SET_TARGET_EMPTY, this.setTargetEmpty);
      EventBus.$on(ApplicationEventTypes.TextObjectEvents.UPDATED, this.onUpdateTextProperties);
      EventBus.$on(ApplicationEventTypes.LeftPanelEvents.CHANGE_SHAPE_COLOR, this.changeShapeColor);
      EventBus.$on(ApplicationEventTypes.LeftPanelEvents.CHANGE_SHAPE_STROKE, this.changeShapeStroke);
      EventBus.$on(ApplicationEventTypes.LeftPanelEvents.CHANGE_SHAPE_STROKE_WIDTH, this.changeStrokeWidth);
      EventBus.$on(ApplicationEventTypes.LeftPanelEvents.PREVIEW_FONT_FAMILY, this.previewFontFamilyOnCanvas);
      EventBus.$on(ApplicationEventTypes.LeftPanelEvents.CLEAR_FONT_FAMILY_PREVIEW, this.clearFontFamilyPreviewOnCanvas);
      EventBus.$on(ApplicationEventTypes.LeftPanelEvents.CHANGE_SVG_FILL_COLOR, this.changeSvgFillColor);
      EventBus.$on(ApplicationEventTypes.LeftPanelEvents.CHANGE_SVG_STROKE_COLOR, this.changeSvgStrokeColor);
      EventBus.$on(ApplicationEventTypes.LeftPanelEvents.CHANGE_SVG_STROKE_COLOR_BY_ID, this.changeSvgStrokeColorById);
      EventBus.$on(ApplicationEventTypes.ImageObjectEvents.REPLACED, this.replaceCanvansPhoto);
      EventBus.$on(ApplicationEventTypes.ImageObjectEvents.REPLACED_ADDRESS_PHOTO, this.replaceCanvansAddressBoxPhoto);
      EventBus.$on(ApplicationEventTypes.ImageObjectEvents.CHANGE_FDIC_PHOTO, this.changeFdicPhoto);
      EventBus.$on(ApplicationEventTypes.ImageObjectEvents.CHANGE_HOUSING_PHOTO, this.changeHousingPhoto);
      EventBus.$on(ApplicationEventTypes.ObjectSelectionEvents.SELECTED_FROM_RIGHT_PANEL, this.selectingCanvasObjectFromRightPanel);
      EventBus.$on(ApplicationEventTypes.ObjectSelectionEvents.CLEARED, this.onClearSelection);
      EventBus.$on(ApplicationEventTypes.ImageObjectEvents.ADDED, this.AddNewObjectImage);
      EventBus.$on(ApplicationEventTypes.TextObjectEvents.TEXT_EDITOR_ADDED, this.addingTextEditor);
      EventBus.$on(ApplicationEventTypes.TextObjectEvents.TEXT_SELECTION_UPDATED, this.getTextSelectionProperties);
      EventBus.$on(ApplicationEventTypes.ObjectSelectionEvents.ZOOM_CHANGE, this.onObjectUpdated);
      EventBus.$on(ApplicationEventTypes.ObjectSelectionEvents.UPDATE_OBJECT_ALIGNMENT, this.alignObjectPosition);
      EventBus.$on(ApplicationEventTypes.ObjectSelectionEvents.ROTATE_OBJECT, this.rotateObject);
      EventBus.$on(ApplicationEventTypes.ObjectSelectionEvents.FLIP_OBJECT, this.flipObject);
      EventBus.$on(ApplicationEventTypes.ObjectSelectionEvents.GROUP_OBJECTS, this.groupObjects);
      EventBus.$on(ApplicationEventTypes.ObjectSelectionEvents.UNGROUP_OBJECTS, this.ungroupObjects);
      EventBus.$on(ApplicationEventTypes.AnimationEvent.SET_ELEMENT_ANIMATION, this.setElementAnimation);
      EventBus.$on(ApplicationEventTypes.Video.ADD_NEW_VIDEO_ON_CANVAS, this.AddNewObjectVideo);
      EventBus.$on(ApplicationEventTypes.Video.TRIM_VIDEO, this.trimVideo);
      EventBus.$on(ApplicationEventTypes.ObjectSelectionEvents.OBJECT_DUPLICATE, this.duplicateObject);
      EventBus.$on('deleteContentReq', this.deleteObject);
      EventBus.$on(ApplicationEventTypes.ObjectSelectionEvents.SELECT_MULTIPLE_OBJECTS, this.selectMultipleObjects);
      EventBus.$on(ApplicationEventTypes.LeftPanelEvents.CHANGE_IMAGE_BORDER_WIDTH_UI, this.changeImageBorderInUI);
      EventBus.$on(ApplicationEventTypes.Canvas.HIDE_BOUNDING_BOX, this.hideBoundingBox);
      EventBus.$on(ApplicationEventTypes.Canvas.RESTORE_BOUNDING_BOX, this.restoreBoundingBoxAfterAnimation);
      EventBus.$on('tableSelectAndEditCell', this.onTableSelectAndEditCell);
      EventBus.$on('tableDragFromInside', this.onTableDragFromInside);
    }
  },
  beforeDestroy() {
    this.destroyContextMenu();
    window.removeEventListener('keydown', this.doCommand);
    document.removeEventListener('dblclick', this.onDocumentDoubleClick, true);
    EventBus.$off(ApplicationEventTypes.LeftPanelEvents.CHANGE_SVG_FILL_COLOR, this.changeSvgFillColor);
    EventBus.$off(ApplicationEventTypes.LeftPanelEvents.CHANGE_SVG_STROKE_COLOR, this.changeSvgStrokeColor);
    EventBus.$off(ApplicationEventTypes.LeftPanelEvents.CHANGE_SVG_STROKE_COLOR_BY_ID, this.changeSvgStrokeColorById);
    EventBus.$off(ApplicationEventTypes.LeftPanelEvents.CHANGE_SHAPE_STROKE_WIDTH, this.changeStrokeWidth);
    EventBus.$off(ApplicationEventTypes.ObjectSelectionEvents.OBJECT_DELETED, this.removeObjectFromCanvas);
    EventBus.$off(ApplicationEventTypes.ImageObjectEvents.CHANGE_FDIC_PHOTO, this.changeFdicPhoto);
    EventBus.$off(ApplicationEventTypes.ImageObjectEvents.CHANGE_HOUSING_PHOTO, this.changeHousingPhoto);
    EventBus.$off(ApplicationEventTypes.ObjectSelectionEvents.SET_TARGET_EMPTY, this.setTargetEmpty);
    EventBus.$off(ApplicationEventTypes.ObjectSelectionEvents.UPDATED, this.onObjectUpdated);
    EventBus.$off(ApplicationEventTypes.TextObjectEvents.UPDATED, this.onUpdateTextProperties);
    EventBus.$off(ApplicationEventTypes.ImageObjectEvents.REPLACED, this.replaceCanvansPhoto);
    EventBus.$off(ApplicationEventTypes.ImageObjectEvents.REPLACED_ADDRESS_PHOTO, this.replaceCanvansAddressBoxPhoto);
    EventBus.$off(ApplicationEventTypes.ObjectSelectionEvents.SELECTED_FROM_RIGHT_PANEL, this.selectingCanvasObjectFromRightPanel);
    EventBus.$off(ApplicationEventTypes.ObjectSelectionEvents.CLEARED, this.onClearSelection);
    EventBus.$off(ApplicationEventTypes.ImageObjectEvents.ADDED, this.AddNewObjectImage);
    EventBus.$off(ApplicationEventTypes.TextObjectEvents.TEXT_EDITOR_ADDED, this.addingTextEditor);
    EventBus.$off(ApplicationEventTypes.TextObjectEvents.TEXT_SELECTION_UPDATED, this.getTextSelectionProperties);
    EventBus.$off(ApplicationEventTypes.LeftPanelEvents.CHANGE_SHAPE_COLOR, this.changeShapeColor);
    EventBus.$off(ApplicationEventTypes.LeftPanelEvents.CHANGE_SHAPE_STROKE, this.changeShapeStroke);
    EventBus.$off(ApplicationEventTypes.ObjectSelectionEvents.ZOOM_CHANGE, this.onObjectUpdated);
    EventBus.$off(ApplicationEventTypes.ObjectSelectionEvents.UPDATE_OBJECT_ALIGNMENT, this.alignObjectPosition);
    EventBus.$off(ApplicationEventTypes.ObjectSelectionEvents.ROTATE_OBJECT, this.rotateObject);
    EventBus.$off(ApplicationEventTypes.ObjectSelectionEvents.FLIP_OBJECT, this.flipObject);
    EventBus.$off(ApplicationEventTypes.ObjectSelectionEvents.GROUP_OBJECTS, this.groupObjects);
    EventBus.$off(ApplicationEventTypes.ObjectSelectionEvents.UNGROUP_OBJECTS, this.ungroupObjects);
    EventBus.$off(ApplicationEventTypes.AnimationEvent.SET_ELEMENT_ANIMATION, this.setElementAnimation);
    EventBus.$off(ApplicationEventTypes.Video.ADD_NEW_VIDEO_ON_CANVAS, this.AddNewObjectVideo);
    EventBus.$off(ApplicationEventTypes.Video.TRIM_VIDEO, this.trimVideo);
    EventBus.$off(ApplicationEventTypes.ObjectSelectionEvents.OBJECT_DUPLICATE, this.duplicateObject);
    EventBus.$off('deleteContentReq', this.deleteObject);
    EventBus.$off(ApplicationEventTypes.ObjectSelectionEvents.SELECT_MULTIPLE_OBJECTS, this.selectMultipleObjects);
    EventBus.$off(ApplicationEventTypes.LeftPanelEvents.CHANGE_IMAGE_BORDER_WIDTH_UI, this.changeImageBorderInUI);
    EventBus.$off(ApplicationEventTypes.Canvas.HIDE_BOUNDING_BOX, this.hideBoundingBox);
    EventBus.$off(ApplicationEventTypes.Canvas.RESTORE_BOUNDING_BOX, this.restoreBoundingBoxAfterAnimation);
    EventBus.$off(ApplicationEventTypes.LeftPanelEvents.PREVIEW_FONT_FAMILY, this.previewFontFamilyOnCanvas);
    EventBus.$off(ApplicationEventTypes.LeftPanelEvents.CLEAR_FONT_FAMILY_PREVIEW, this.clearFontFamilyPreviewOnCanvas);
    this.removeClickEventToMoveableControls();
    EventBus.$off('tableSelectAndEditCell', this.onTableSelectAndEditCell);
    EventBus.$off('tableDragFromInside', this.onTableDragFromInside);
  },
  methods: {
    onTableDragFromInside({ tableId, inputEvent }) {
      const el  = document.getElementById(tableId);
      const obj = findObjectById(this.objectList, tableId);
      if (!el || !obj || !this.moveableObj || !this.canTransformObject(obj)) { return; }
      this.moveableObj.dragStart(inputEvent, el);
    },
    onTableSelectAndEditCell({ tableId, r, c }) {
      if (!tableId) { return; }

      const obj = findObjectById(this.objectList, tableId);
      if (!obj || obj.type !== 'table' || obj.locked) { return; }

      const needsSelect =
          !this.activeObject
          || this.activeObject.Id !== tableId
          || !obj.isSelected;

      if (needsSelect) {
        if (findParentGroup(this.objectList, tableId)) {
          this.selectGroupChildById(tableId);
        } else {
          EventBus.$emit('onObjectSelectedFromRightPanel', tableId);
        }
      }

      this.$nextTick(() => {
        EventBus.$emit('tableStartCellEdit', { tableId, r, c });
      });
    },
    groupObjects() {
      // Prefer the shared product list (same order as Layers panel)
      if (this.contentList && this.objectList !== this.contentList) {
        this.objectList = this.contentList;
      }

      const selected = this.objectList.filter(o => o.isSelected);
      const selectedGroup = selected.find(o => o.type === 'group')
          || this.objectList.find(o => o.type === 'group' && o.children && o.children.some(child => child.isSelected));
      const selectedLayers = selected.filter(o => o.type !== 'group');

      if (selectedGroup && selectedLayers.length) {
        const updatedGroup = addLayersToExistingGroup(selectedGroup, selectedLayers);
        const selectedLayerIds = new Set(selectedLayers.map(o => o.Id));
        const groupIndex = this.objectList.findIndex(o => o.Id === selectedGroup.Id);

        if (groupIndex === -1) { return; }

        this.objectList[groupIndex] = updatedGroup;
        for (let i = this.objectList.length - 1; i >= 0; i -= 1) {
          if (selectedLayerIds.has(this.objectList[i].Id)) {
            this.objectList.splice(i, 1);
          }
        }
        this.makeIsSelectedKeyFalse();
        updatedGroup.isSelected = true;
        this.activeObject = updatedGroup;

        EventBus.$emit(ApplicationEventTypes.LayerPanelEvents.UPDATE_DATA_ON_LAYER_SWAP, this.objectList);
        EventBus.$emit(ApplicationEventTypes.Canvas.CANVAS_RE_RENDER);
        this.$nextTick(() => {
          this.applyMultiSelectMoveableTargets([updatedGroup]);
          EventBus.$emit('onObjectSelectedFromRightPanel', updatedGroup.Id);
        });
        this.emitUndoRedoRequest('called by addSelectionToGroup');
        return;
      }
      const layersInStackOrder = this.objectList.filter(o => o.isSelected && o.type !== 'group');
      if (!canGroupObjects(layersInStackOrder)) { return; }

      const group       = GroupObject.createGroupFromObjects(layersInStackOrder, this.objectList);
      const selectedIds = new Set(layersInStackOrder.map(o => o.Id));
      const anchorId    = (this.selectionAnchorId && selectedIds.has(this.selectionAnchorId))
          ? this.selectionAnchorId
          : (layersInStackOrder[0] && layersInStackOrder[0].Id);

      // Mutate in place so Canvas stays synced with product.objectList / Layers panel
      replaceLayersWithGroup(this.objectList, layersInStackOrder, group, anchorId);

      this.makeIsSelectedKeyFalse();
      group.isSelected       = true;
      this.activeObject      = group;
      this.selectionAnchorId = group.Id;

      EventBus.$emit(ApplicationEventTypes.LayerPanelEvents.UPDATE_DATA_ON_LAYER_SWAP, this.objectList);
      EventBus.$emit(ApplicationEventTypes.Canvas.CANVAS_RE_RENDER);

      this.$nextTick(() => {
        this.applyMultiSelectMoveableTargets([group]);
        EventBus.$emit('onObjectSelectedFromRightPanel', group.Id);
      });

      this.emitUndoRedoRequest('called by groupObjects');
    },

    ungroupObjects() {
      const selectedGroupIds = this.objectList
          .filter(o => o.isSelected && o.type === 'group')
          .map(o => o.Id);
      if (!selectedGroupIds.length) { return; }

      const selectedIdSet = new Set(selectedGroupIds);
      const rebuilt = [];
      this.objectList.forEach((item) => {
        if (item.type === 'group' && selectedIdSet.has(item.Id)) {
          rebuilt.push(...flattenGroup(item));
        } else {
          rebuilt.push(item);
        }
      });
      this.objectList.splice(0, this.objectList.length, ...rebuilt);

      this.makeIsSelectedKeyFalse();
      EventBus.$emit(ApplicationEventTypes.ObjectSelectionEvents.CLEARED);
      EventBus.$emit(ApplicationEventTypes.LayerPanelEvents.UPDATE_DATA_ON_LAYER_SWAP, this.objectList);
      EventBus.$emit(ApplicationEventTypes.Canvas.CANVAS_RE_RENDER);
      this.emitUndoRedoRequest('called by ungroupObjects');
    },

    cloneCanvasObject(tempObj) {
      const obj = tempObj.type === 'group'
          ? reassignGroupIds(tempObj)
          : _.cloneDeep(tempObj);

      const oldId = tempObj.Id;
      if (obj.type === 'table') {
        obj.Name = getNextTableName(this.objectList);
        obj.Id = uuid.v1();
      }else if (obj.type === 'group') {
        obj.Name = getUniqueGroupName(this.objectList, 'Group');
        if (Array.isArray(obj.children)) {
          obj.children.forEach((child) => { child.isSelected = false; });
        }
      } else {
        let nameExist = true;
        do {
          if (this.objectList.find(item => item.Name === obj.Name)) {
            obj.Name += ' copy';
          } else {
            nameExist = false;
          }
        } while (nameExist);
        obj.Id = uuid.v1();
      }

      obj.isSelected = false;
      obj.editor     = null;
      obj.PageNo     = this.getActivePageIndex;
      obj.X         += 30;
      obj.Y         += 30;
      if (obj.type === 'group' && Array.isArray(obj.children)) {
        obj.children.forEach((child) => {
          child.X = (child.X || 0) + 30;
          child.Y = (child.Y || 0) + 30;
        });
        recalculateGroupBounds(obj);
      }

      if (obj.type === 'text' && obj.modifiedHtml !== obj.Value) {
        obj.Value = obj.modifiedHtml;
      }

      if (obj.type === 'image'
          && (obj.ImageType === 'SVGImage' || obj.ImageType.indexOf('FDIC') > -1)
          && obj.SvgProperty
          && obj.SvgProperty.content
          && obj.isGradient) {
        obj.SvgProperty.content = obj.SvgProperty.content.replaceAll(oldId, obj.Id);
      }

      return obj;
    },
    getSelectedGroupChildren() {
      const selectedChildren = [];

      this.objectList.forEach((group) => {
        if (group.type !== 'group' || !group.children) { return; }

        group.children.forEach((child) => {
          if (child.isSelected && !child.locked) {
            selectedChildren.push({ group, child });
          }
        });
      });

      return selectedChildren;
    },
    insertChildCloneInGroup(group, child) {
      const childClone = this.cloneCanvasObject(child);
      const childIndex = group.children.findIndex(item => item.Id === child.Id);

      group.children.splice(childIndex + 1, 0, childClone);
      recalculateGroupBounds(group);

      return childClone;
    },
    removeSelectedChildrenFromGroup(group) {
      const childCount = group.children.length;
      group.children = group.children.filter(child => !(child.isSelected && !child.locked));

      if (group.children.length === childCount) { return false; }
      if (group.children.length) {
        recalculateGroupBounds(group);
      }
      return true;
    },
    duplicateObject() {
      if (this.contentList && this.objectList !== this.contentList) {
        this.objectList = this.contentList;
      }

      const selectedList          = this.objectList.filter(element => element.isSelected === true);
      const selectedGroupChildren = this.getSelectedGroupChildren();

      selectedList.reverse();

      selectedList.forEach((tempObj) => {
        this.objectList.unshift(this.cloneCanvasObject(tempObj));
      });

      selectedGroupChildren.forEach(({ group, child }) => {
        this.insertChildCloneInGroup(group, child);
      });

      if (!selectedList.length && !selectedGroupChildren.length) {
        return;
      }
      EventBus.$emit(ApplicationEventTypes.LayerPanelEvents.UPDATE_DATA_ON_LAYER_SWAP, this.objectList);
      this.$nextTick(() => {
        if (this.moveableObj) {
          this.moveableObj.target = null;
          if (typeof this.moveableObj.updateTarget === 'function') {
            this.moveableObj.updateTarget();
          }
        }
        this.refreshMoveableTargets();
      });
      this.emitUndoRedoRequest('called by duplicateLayer');
    },
    deleteObject(objectIndex) {
      if (objectIndex && objectIndex.type === 'groupChild') {
        const group = this.objectList.find(item => item.Id === objectIndex.groupId && item.type === 'group');
        if (!group || !group.children) { return; }

        group.children = group.children.filter(child => child.Id !== objectIndex.childId);
        if (group.children.length) {
          recalculateGroupBounds(group);
        } else {
          const groupIndex = this.objectList.findIndex(item => item.Id === group.Id);
          if (groupIndex !== -1) {
            this.objectList.splice(groupIndex, 1);
          }
        }

        EventBus.$emit(ApplicationEventTypes.ObjectSelectionEvents.CLEARED);
        EventBus.$emit(ApplicationEventTypes.LayerPanelEvents.UPDATE_DATA_ON_LAYER_SWAP, this.objectList);
        EventBus.$emit(ApplicationEventTypes.Canvas.CANVAS_RE_RENDER);
        this.emitUndoRedoRequest('called by delete');
        return;
      }

      const toRemove             = new Set();
      let selectedElementDeleted = false;
      if (objectIndex === -1) {
        this.objectList.forEach((element, index) => {
          const isActiveGroup = this.activeObject
              && this.activeObject.type === 'group'
              && this.activeObject.Id === element.Id;

          if ((element.isSelected || isActiveGroup) && !element.locked) {
            toRemove.add(index); if (element.isSelected) { selectedElementDeleted = true; }
            selectedElementDeleted = true;
            return;
          }
          if (element.type === 'group' && element.children) {
            if (this.removeSelectedChildrenFromGroup(element)) {
              selectedElementDeleted = true;
              if (!element.children.length) {
                toRemove.add(index);
              }
            }
          }
        });
      } else if (objectIndex > -1 && objectIndex < this.objectList.length) {
        toRemove.add(objectIndex);
      }
      const indexesToRemove = Array.from(toRemove);
      for (let i = indexesToRemove.length - 1; i >= 0; i--) {
        const removedElement = this.objectList.splice(indexesToRemove[i], 1);
        if (removedElement.length > 0 && removedElement[0].isSelected) { selectedElementDeleted = true; }
      }

      if (selectedElementDeleted) { EventBus.$emit(ApplicationEventTypes.ObjectSelectionEvents.CLEARED); }
      EventBus.$emit(ApplicationEventTypes.LayerPanelEvents.UPDATE_DATA_ON_LAYER_SWAP, this.objectList);
      EventBus.$emit(ApplicationEventTypes.Canvas.CANVAS_RE_RENDER);
      this.emitUndoRedoRequest('called by delete');
    },
    async pasteCopiedObjects() {
      if (this.contentList && this.objectList !== this.contentList) {
        this.objectList = this.contentList;
      }

      let copiedObjects = _.cloneDeep(this.$store.getters.getCopiedObjects || []);
      try {
        const clipboardText = await navigator.clipboard.readText();
        if (clipboardText) {
          const parsed = JSON.parse(clipboardText);
          if (Array.isArray(parsed) && parsed.length > 0) {
            copiedObjects = _.cloneDeep(parsed);
          }
        }
      } catch (err) {
        console.warn('Failed to read from clipboard', err);
      }
      if (!copiedObjects || !copiedObjects.length) { return; }

      const pastedObjects = [];
      [...copiedObjects].reverse().forEach((tempObj) => {
        const clone = this.cloneCanvasObject(tempObj);
        this.objectList.unshift(clone);
        pastedObjects.push(clone);
      });
      EventBus.$emit(ApplicationEventTypes.LayerPanelEvents.UPDATE_DATA_ON_LAYER_SWAP, this.objectList);
      if (pastedObjects.length === 1) {
        EventBus.$emit(ApplicationEventTypes.Canvas.CANVAS_RE_RENDER);
      }
      this.emitUndoRedoRequest('called by pasteCopiedObjects');

      this.$nextTick(() => {
        if (pastedObjects.length === 1) {
          EventBus.$emit('onObjectSelectedFromRightPanel', pastedObjects[0].Id);
          return;
        }
        if (pastedObjects.length > 1) {
          this.selectMultipleObjects(pastedObjects);
        }
      });
    },
    deleteCopiedLayers(copiedObjects) {
      EventBus.$emit(ApplicationEventTypes.ObjectSelectionEvents.CLEARED);
      copiedObjects.forEach((copiedObj) => {
        const index = this.objectList.findIndex(layer => layer.Id === copiedObj.Id);
        if (index !== -1) {
          this.objectList.splice(index, 1);
          return;
        }
        const parent = findParentGroup(this.objectList, copiedObj.Id);
        if (parent && parent.children) {
          const childIndex = parent.children.findIndex(child => child.Id === copiedObj.Id);
          if (childIndex !== -1) {
            parent.children.splice(childIndex, 1);
            recalculateGroupBounds(parent);
          }
        }
      });
      EventBus.$emit(ApplicationEventTypes.LayerPanelEvents.UPDATE_DATA_ON_LAYER_SWAP, this.objectList);
      this.emitUndoRedoRequest('called by delete Copied Objects');
    },
    trimVideo({ startTime, endTime }) {
      this.activeObject.videoDetails.startTime = parseFloat(startTime);
      this.activeObject.videoDetails.endTime   = parseFloat(endTime);
      EventBus.$emit(ApplicationEventTypes.ProductPageEvents.ACTIVE_PAGE_DURATION_UPDATE);
    },
    updateOnWindowResize() {
      console.log('update canvas');
      setTimeout(() => {
        this.moveableObj.updateRect();
      });
    },
    updateMoveableTargets() {
      const { target } =  this.moveableObj;
      if (target && target.length > 0) {
        const object = findObjectById(this.objectList, target[0].id)
            || this.objectList[parseInt(target[0].dataset.elementindex, 10)];
        object && object.type === 'text' && (object.Value = object.modifiedHtml);
      }
      this.targets = [];
      if (this.isTextEditModeActive && this.isTextEditModeActive()) {
        this.hideMoveableDuringTextEdit();
        setTimeout(() => {
          this.getTextSelectionProperties();
        });
        return;
      }
      if (this.activeObject && !this.hasTransformableSelection()) {
        this.syncMoveableForLockState();
        return;
      }
      if (this.activeObject) {
        const selectedTopLevel = this.getSelectedTopLevelObjects();
        const selectedMoveable = this.getSelectedMoveableObjects();
        const parentGroup      = findParentGroup(this.objectList, this.activeObject.Id);
        if (selectedMoveable.length > 1 || selectedTopLevel.length > 1) {
          this.targets = this.buildMoveableTargetsForObjects(selectedMoveable);
          this.moveableObj.target = this.targets.length ? this.targets : null;
          this.moveableObj.updateTarget();
          if (typeof this.moveableObj.updateRect === 'function') {
            this.moveableObj.updateRect();
          }
        } else if (parentGroup) {
          const el = document.getElementById(this.activeObject.Id);
          if (el) {
            this.targets = [el];
          }
        } else if (this.activeObject.type === 'group' && this.canTransformObject(this.activeObject)) {
          this.targets = this.buildMoveableTargetsForObjects([this.activeObject]);
          this.moveableObj.target = this.targets.length ? this.targets : null;
          this.moveableObj.resizable        = false;
          this.moveableObj.scalable         = true;
          this.moveableObj.keepRatio        = true;
          this.moveableObj.renderDirections = ['nw', 'ne', 'se', 'sw'];
          this.moveableObj.updateTarget();
          if (typeof this.moveableObj.updateRect === 'function') {
            this.moveableObj.updateRect();
          }
        } else {
          const selectedItem = this.objectList.filter(elem => elem.isSelected && elem.IsVisible && !elem.locked);
          this.targets       = selectedItem.map(item => document.getElementById(item.Id)).filter(Boolean);
        }
      }
      this.moveableObj.target = this.targets.length === 1
          ? this.targets[0]
          : (this.targets.length ? this.targets : null);
      if (this.activeObject && this.activeObject.type === 'text') {
        setTimeout(() => {
          this.getTextSelectionProperties();
        });
      }
      setTimeout(() => {
        if (this.isTextEditModeActive && this.isTextEditModeActive()) {
          this.hideMoveableDuringTextEdit();
          return;
        }
        this.moveableObj.updateRect();
        this.refreshDragTarget();
      }, 0);
    },
    rotateObject(direction) {
      if (direction == 1) { this.moveableObj.request('rotatable', { deltaRotate: 5 }, true); }
      if (direction == -1) { this.moveableObj.request('rotatable', { deltaRotate: -5 }, true); }

      EventBus.$emit(ApplicationEventTypes.LayerPanelEvents.UPDATE_DATA_ON_LAYER_SWAP, this.objectList);
      this.emitUndoRedoRequest('Called by rotate end');
    },
    flipObject(direction) {
      if (direction == 1) {
        if (this.activeObject.IsHFlipped == false) { this.activeObject.IsHFlipped = true; } else { this.activeObject.IsHFlipped = false; }
      }
      if (direction == -1) {
        if (this.activeObject.IsVFlipped == false) { this.activeObject.IsVFlipped = true; } else { this.activeObject.IsVFlipped = false; }
      }

      this.onObjectUpdated();
      this.$nextTick(() => {
        try {
          if (this.activeObject && this.activeObject.effect === 'shadow' && this.activeObject.shadow) {
            const elem = document.querySelector(`.canvas-main-html-container [id='${this.activeObject.Id}']`);
            if (elem) {
              const s = this.activeObject.shadow;
              if (typeof s.shadowOffsetX !== 'undefined' && typeof s.shadowOffsetY !== 'undefined' && typeof s.blur !== 'undefined' && s.color) {
                elem.style.filter = `drop-shadow(${s.shadowOffsetX}px ${s.shadowOffsetY}px ${s.blur * 40}px ${s.color})`;
              }
            }
          }
        } catch (e) {
        }
      });
      this.emitUndoRedoRequest('called by Flip Update');
    },
    alignObjectPosition(param) {
      const self = this;
      const list = (this.selectedObjectsList && this.selectedObjectsList.length)
          ? this.selectedObjectsList
          : ((this.activeObject && this.activeObject.type === 'group' && this.activeObject.children)
              ? this.activeObject.children
              : null);

      if (list && list.length > 1) {
        const touchedGroupsById = new Map();
        const objectList        = this.product.pages[this.activePage].objectList;
        list.forEach((object) => {
          const obj = findObjectById(objectList, object.Id);
          if (!obj) { return; }
          this.alignObjectPositionById(param, obj.Id, { skipGroupBounds: true });
          const parent = findParentGroup(objectList, obj.Id);
          if (parent) { touchedGroupsById.set(parent.Id, parent); }
        });
        touchedGroupsById.forEach((parentGroup) => {
          recalculateGroupBounds(parentGroup);
        });
        this.emitUndoRedoRequest('called by Position Update');
        this.$nextTick(() => {
          const topLevel = resolveTopLevelSelection(objectList, list);
          if (topLevel.length > 1) {
            self.selectMultipleObjects(topLevel);
          } else if (topLevel.length === 1) {
            self.refreshSelectionAfterAlign(topLevel[0]);
          } else if (self.activeObject) {
            self.refreshSelectionAfterAlign(self.activeObject);
          }
        });
        return;
      }

      if (this.activeObject && this.activeObject.type === 'group') {
        const group    = this.activeObject;
        const children = list && list.length ? list : (group.children || []);
        if (children.length) {
          children.forEach((object) => {
            const obj = findObjectById(this.product.pages[this.activePage].objectList, object.Id);
            if (!obj) { return; }
            this.alignObjectPositionById(param, obj.Id, { skipGroupBounds: true });
          });
          recalculateGroupBounds(group);
          this.emitUndoRedoRequest('called by Position Update');
          this.$nextTick(() => {
            self.refreshSelectionAfterAlign(group);
          });
        }
        return;
      }

      if (!this.activeObject) { return; }
      this.alignObjectPositionById(param, this.activeObject.Id);
      this.emitUndoRedoRequest('called by Position Update');

      this.$nextTick(() => {
        self.refreshSelectionAfterAlign(self.activeObject);
      });
    },
    refreshSelectionAfterAlign(singleTarget = null, multiList = null) {
      if (singleTarget && singleTarget.type === 'group') {
        const group = findObjectById(this.objectList, singleTarget.Id) || singleTarget;
        this.makeIsSelectedKeyFalse();
        group.isSelected = true;
        if (Array.isArray(group.children)) {
          group.children.forEach((child) => {
            child.isSelected = false;
          });
        }
        this.activeObject = group;
        this.applyMultiSelectMoveableTargets([group]);
        this.activeObject = group;
        if (this.moveableObj && typeof this.moveableObj.updateRect === 'function') {
          this.moveableObj.updateRect();
        }
        this.refreshDragTarget();
        EventBus.$emit(ApplicationEventTypes.ObjectSelectionEvents.SELECTED, this.activeObject);
        EventBus.$emit(ApplicationEventTypes.LayerPanelEvents.UPDATE_DATA_ON_LAYER_SWAP, this.objectList);
        return;
      }

      if (multiList && multiList.length > 1) {
        this.selectMultipleObjects(multiList);
        return;
      }

      if (singleTarget) {
        const activeObject = findObjectById(this.objectList, singleTarget.Id) || singleTarget;
        if (this.moveableObj && !this.canTransformObject(activeObject)) {
          this.syncMoveableForLockState();
        } else if (this.moveableObj) {
          const el = document.getElementById(activeObject.Id);
          if (el) {
            this.moveableObj.target = el;
            this.moveableObj.updateTarget();
            if (typeof this.moveableObj.updateRect === 'function') {
              this.moveableObj.updateRect();
            }
          }
          this.refreshDragTarget();
        }
        this.refreshMoveableTargets();
        EventBus.$emit(ApplicationEventTypes.ObjectSelectionEvents.SELECTED, activeObject);
        EventBus.$emit(ApplicationEventTypes.LayerPanelEvents.UPDATE_DATA_ON_LAYER_SWAP, this.objectList);
      }
    },
    selectMultipleObjects(payload) {
      const objects = Array.isArray(payload) ? payload : (payload && payload.objects);
      const anchorId = Array.isArray(payload) ? null : (payload && payload.anchorId);
      const keepGroupChildren = !Array.isArray(payload) && !!(payload && payload.keepGroupChildren);
      if (!objects || !objects.length) {
        return;
      }

      // Stay on the shared product list so grouping uses the same order as Layers
      if (this.contentList && this.objectList !== this.contentList) {
        this.objectList = this.contentList;
      }

      const selectedIdSet = new Set(
          objects.map(obj => (obj && (obj.Id || obj))).filter(Boolean),
      );

      this.makeIsSelectedKeyFalse();
      const topLevelById = new Map();
      const selectedChildObjects = [];
      objects.forEach((obj) => {
        const found = findObjectById(this.objectList, obj.Id || obj);
        if (!found) {
          return;
        }
        if (found.type === 'group') {
          found.isSelected = true;
          if (this.canTransformObject(found)) {
            topLevelById.set(found.Id, found);
          }
          return;
        }
        const parentGroup = keepGroupChildren ? null : findParentGroup(this.objectList, found.Id);
        if (parentGroup && !selectedIdSet.has(parentGroup.Id)) {
          found.isSelected = true;
          parentGroup.isSelected = false;
          selectedChildObjects.push(found);
          return;
        }
        const topLevel = parentGroup || found;
        topLevel.isSelected = true;
        if (this.canTransformObject(topLevel)) {
          topLevelById.set(topLevel.Id, topLevel);
        }
      });

      const selected = Array.from(topLevelById.values());
      const moveableChildren = selectedChildObjects.filter(child => this.canTransformObject(child));
      const moveableObjects = selected.concat(moveableChildren);
      this.selectionAnchorId = (anchorId && (
          selected.some(item => item.Id === anchorId)
          || selectedChildObjects.some(item => item.Id === anchorId)
      ))
          ? anchorId
          : (moveableObjects[0] && moveableObjects[0].Id)
          || (selectedChildObjects[0] && selectedChildObjects[0].Id)
          || null;
      const anchorObject = this.selectionAnchorId
          ? findObjectById(this.objectList, this.selectionAnchorId)
          : null;
      this.activeObject = (anchorObject && this.canTransformObject(anchorObject))
          ? anchorObject
          : (moveableObjects[0] || anchorObject || selectedChildObjects[0] || null);

      EventBus.$emit(ApplicationEventTypes.LayerPanelEvents.UPDATE_DATA_ON_LAYER_SWAP, this.objectList);

      this.$nextTick(() => {
        if (selected.length === 1 && selected[0].type === 'group' && !selectedChildObjects.length) {
          const group = selected[0];
          this.makeIsSelectedKeyFalse();
          group.isSelected = true;
          if (Array.isArray(group.children)) {
            group.children.forEach((child) => {
              child.isSelected = false;
            });
          }
          this.activeObject = group;
          this.applyMultiSelectMoveableTargets([group]);
          // Selecto 'select' can clear activeObject — keep the group selected.
          this.activeObject = group;
          group.isSelected = true;
          EventBus.$emit(ApplicationEventTypes.ObjectSelectionEvents.SELECTED, group);
          EventBus.$emit(ApplicationEventTypes.LayerPanelEvents.UPDATE_DATA_ON_LAYER_SWAP, this.objectList);
          return;
        }

        if (selectedChildObjects.length) {
          selectedChildObjects.forEach((child) => { child.isSelected = true; });
          selectedChildObjects.forEach((child) => {
            const parent = findParentGroup(this.objectList, child.Id);
            if (parent) {
              parent.isSelected = false;
            }
          });
          this.activeObject = (anchorObject && this.canTransformObject(anchorObject))
              ? anchorObject
              : (moveableObjects[0] || selectedChildObjects[0]);

          const selectedCount = selected.length + selectedChildObjects.length;
          if (selectedCount === 1) {
            this.selectGroupChildById(selectedChildObjects[0].Id);
            return;
          }

          const moveableTargets = this.applyMultiSelectMoveableTargets(
              moveableObjects.length ? moveableObjects : selectedChildObjects,
          );
          EventBus.$emit(ApplicationEventTypes.ObjectSelectionEvents.SELECTED, this.activeObject);
          if (moveableTargets && moveableTargets.length) {
            EventBus.$emit(
                ApplicationEventTypes.ObjectSelectionEvents.SELECTED_OBJETCS,
                [moveableTargets],
            );
          }
          EventBus.$emit(ApplicationEventTypes.LayerPanelEvents.UPDATE_DATA_ON_LAYER_SWAP, this.objectList);
          return;
        }

        const selectoTargets = objects
            .map(item => document.getElementById(item.Id))
            .filter(Boolean);

        this.suppressSelectoSelectEnd = keepGroupChildren && selected.length > 1
            && selected.some(item => findParentGroup(this.objectList, item.Id));

        if (this.selectO && selectoTargets.length) {
          this.selectO.setSelectedTargets(selectoTargets);
        }

        const clickEvent = new MouseEvent('click', {
          view: window,
          bubbles: true,
          cancelable: true,
        });
        this.selectO.trigger('selectEnd', {
          inputEvent: clickEvent,
          selected: selectoTargets,
          isDragStart: false,
          removed: [],
        });
        this.suppressSelectoSelectEnd = false;

        if (selected.length > 1) {
          this.applyMultiSelectMoveableTargets(selected);
          if (this.activeObject) {
            EventBus.$emit(ApplicationEventTypes.ObjectSelectionEvents.SELECTED, this.activeObject);
          }
          EventBus.$emit(
              ApplicationEventTypes.ObjectSelectionEvents.SELECTED_OBJETCS,
              [this.selectO.selectedTargets],
          );
        }
      });
    },
    alignObjectPositionById(param, objectId, options = {}) {
      const obj = findObjectById(this.product.pages[this.activePage].objectList, objectId);
      if (!obj) {
        return;
      }

      if (obj.type == 'text' && obj.shapeType && obj.shapeType == 'curve' && obj.isEditorEnable) {
        obj.isEditorEnable = false;
        const selector = `.canvas-non-play-all [id='${obj.Id}'] > div`;
        const elem = document.querySelector(selector);
        obj.unCurvedHtml = elem.innerHTML;
        this.traverseAndWrap(elem);
        this.curveText(elem, obj.curve, obj);


        setTimeout(() => {
          const newDimensions = this.adjustTargetElement(`.canvas-non-play-all [id='${obj.Id}']`, obj);

          const updatedHtml = elem.innerHTML;

          obj.Value = updatedHtml;
          obj.modifiedHtml = updatedHtml;
          obj.curvedHtml = updatedHtml;

          this.applyCurvedBoundsToTextObject(this.objectList, obj, newDimensions);
          this.refreshMoveableTargets();
        });
      }

      const objectEl = document.getElementById(obj.Id);
      const mainHtml = document.getElementById('main_html');
      if (!objectEl || !mainHtml) {
        return;
      }

      const objectElement = objectEl.getBoundingClientRect();
      const mainHtmlElement = mainHtml.getBoundingClientRect();
      const scale = new DOMMatrix(mainHtml.style.transform).m11;
      let diff = 0;
      const {productCrop} = param;
      const parentGroup = findParentGroup(this.objectList, objectId);
      const skipGroupBounds = !!(options && options.skipGroupBounds);

      switch (param.value) {
        case 'left':
          diff = Math.ceil((mainHtmlElement.left - objectElement.left) / scale);
          obj.X += productCrop.AllowCropping ? Number(productCrop.XCrop) + diff : diff;
          break;
        case 'center':
          diff = ((mainHtmlElement.left + mainHtmlElement.width / 2)
              - (objectElement.left + objectElement.width / 2)) / scale;
          obj.X += diff;
          break;
        case 'right':
          diff = parseFloat((mainHtmlElement.right - objectElement.right) / scale);
          obj.X += diff;
          break;
        case 'top':
          diff = parseFloat((mainHtmlElement.top - objectElement.top) / scale);
          obj.Y += productCrop.AllowCropping ? Number(productCrop.YCrop) + diff : diff;
          console.log('top is ', obj.Y);
          break;
        case 'middle':
          diff = ((mainHtmlElement.top + mainHtmlElement.height / 2)
              - (objectElement.top + objectElement.height / 2)) / scale;
          obj.Y += diff;
          break;
        case 'bottom':
          diff = parseFloat((mainHtmlElement.bottom - objectElement.bottom) / scale);
          obj.Y += diff;
          break;
        default:
          break;
      }

      if (obj.type === 'text' && obj.shapeType === 'curve' && !obj.isEditorEnable) {
        this.$nextTick(() => {
          const dims = this.adjustTargetElement(`.canvas-non-play-all [id='${obj.Id}']`, obj);
          if (dims) {
            this.applyCurvedBoundsToTextObject(this.objectList, obj, dims);
            this.refreshMoveableTargets();
          }
        });
      }

      if (parentGroup && !skipGroupBounds) {
        recalculateGroupBounds(parentGroup);
      }

      // EventBus.$emit(ApplicationEventTypes.LayerPanelEvents.UPDATE_DATA_ON_LAYER_SWAP, productClone.pages[this.activePage].objectList);

      if (!skipGroupBounds) {
        this.refreshMoveableTargets();
      }
    },
    isTableKeyboardActive() {
      const el = document.activeElement;
      return !!(el && el.isContentEditable && el.closest && el.closest('.table-object'));
    },
    doCommand(e) {
      console.log('Listen to keyboard', e);
      const focussedElement = document.activeElement;
      const tagName = focussedElement.tagName.toLowerCase();
      if (this.isTableKeyboardActive()) {
        return;
      }

      const isArrowKey = [37, 38, 39, 40].includes(e.keyCode);
      if (!isArrowKey && (tagName === 'textarea' || (tagName === 'input' && !focussedElement.classList.contains('layer-input')))) {
        return;
      }
      const isCtrlKey = e.metaKey || e.ctrlKey;

      console.log('CTRl Key is  ', isCtrlKey);
      if (isCtrlKey && !this.activeEditor) {
        switch (e.keyCode) {
          case 89:
            EventBus.$emit(ApplicationEventTypes.UndoRedoEvents.PRODUCT_UPDATED, 'redo');
            EventBus.$emit(ApplicationEventTypes.ObjectSelectionEvents.MULTI_OBJETCS_CLEAR);
            e.preventDefault();
            break;
          case 90:
            EventBus.$emit(ApplicationEventTypes.UndoRedoEvents.PRODUCT_UPDATED, 'undo');
            EventBus.$emit(ApplicationEventTypes.ObjectSelectionEvents.MULTI_OBJETCS_CLEAR);
            e.preventDefault();
            break;
          case 86:
            this.pasteCopiedObjects();
            e.preventDefault();
            break;
          case 67:
            this.copyByKey('copy');
            e.preventDefault();
            break;
          case 88:
            this.copyByKey('cut');
            e.preventDefault();
            break;
          case 65:
            this.selectAll();
            e.preventDefault();
            break;
          case 71: // Ctrl+G / Ctrl+Shift+G
            if (e.shiftKey) {
              this.ungroupObjects();
            } else {
              this.groupObjects();
            }
            e.preventDefault();
            break;
          default:
            break;
        }
      }
      let multiSelect = false;
      this.objectList.forEach((item) => {
        if (item.isSelected) {
          multiSelect = true;
        }
        if (item.type === 'group' && item.children && item.children.some(child => child.isSelected)) {
          multiSelect = true;
        }
      });
      console.log('Number of actve objects are ', multiSelect);
      if (!multiSelect || this.$store.getters.getDisplayQRCodeModal || this.activeEditor) {
        return;
      }
      const delta = Math.ceil(this.$store.getters.getZoomLevel / 100);
      console.log('New keycode is ', e.keyCode);
      switch (e.keyCode) {
        case 37:
          this.moveableObj.request('draggable', {deltaX: -delta, deltaY: 0}, true);
          e.preventDefault();
          break;
        case 38:
          this.moveableObj.request('draggable', {deltaX: 0, deltaY: -delta}, true);
          e.preventDefault();
          break;
        case 39:
          this.moveableObj.request('draggable', {deltaX: delta, deltaY: 0}, true);
          e.preventDefault();
          break;
        case 40:
          this.moveableObj.request('draggable', {deltaX: 0, deltaY: delta}, true);
          e.preventDefault();
          break;
        case 46:
        case 8:
          EventBus.$emit(ApplicationEventTypes.ModalEvents.TOGGLE_CONFIRMATION_MODAL, {
            request: 'DeleteReq',
            data: -1,
            message: 'Are you sure you want to delete selected Layer(s)?',
            isVideo: true,
          });
          e.preventDefault();
          break;
        default:
          break;
      }

      if (e.key == 'Escape') {
        console.log('Escape key is pressed', this.$store.state.colorpicker.isopen);
        if (this.$store.state.colorpicker.isopen == true) {
          this.$store.commit('setIsOpen', false);
        }
      }
    },
    selectAll() {
      this.onClearSelection();
      this.objectList.forEach((item) => {
        item.isSelected = true;
      });
      EventBus.$emit(ApplicationEventTypes.LayerPanelEvents.UPDATE_DATA_ON_LAYER_SWAP, this.objectList);
      this.$nextTick(() => {
        const elements = [];
        this.objectList.forEach((item) => {
          if (!item.locked) {
            elements.push(document.getElementById(item.Id));
          }
        });
        // this.selectO.setSelectedTargets(elements);
        const clickEvent = new MouseEvent('click', {
          view: window,
          bubbles: true,
          cancelable: true,

        });

        this.selectO.trigger('selectEnd', {
          inputEvent: clickEvent,
          selected: elements,
          isDragStart: false,
          removed: [],
        });
      });
    },
    copyByKey(action) {
      const tempArray = [];
      const seen = new Set();

      const addClone = (item) => {
        if (!item || seen.has(item.Id) || item.type === 'pagedetails') {
          return;
        }
        seen.add(item.Id);
        // Keep original Ids so cut can delete them; paste reassigns via cloneCanvasObject.
        tempArray.push(_.cloneDeep(item));
      };

      if (typeof this.getSelectedTopLevelObjects === 'function') {
        this.getSelectedTopLevelObjects().forEach(addClone);
      }

      if (typeof this.getSelectedGroupChildren === 'function') {
        this.getSelectedGroupChildren().forEach(({child}) => addClone(child));
      }

      if (!tempArray.length && this.moveableObj && this.moveableObj.target) {
        const raw = this.moveableObj.target;
        const targets = Array.isArray(raw) ? raw : [raw];
        targets.forEach((el) => {
          if (!el || !el.id) {
            return;
          }
          const found = findObjectById(this.objectList, el.id);
          if (!found) {
            return;
          }
          const parent = findParentGroup(this.objectList, found.Id);
          addClone(parent && parent.isSelected ? parent : found);
        });
      }

      if (!tempArray.length && this.activeObject) {
        const parent = findParentGroup(this.objectList, this.activeObject.Id);
        addClone(parent && parent.isSelected ? parent : this.activeObject);
      }

      if (!tempArray.length) {
        return;
      }

      this.$store.commit('updateCopiedObjects', tempArray);
      try {
        const textToCopy = JSON.stringify(tempArray, null, 2);
        navigator.clipboard.writeText(textToCopy)
            .then(() => {
              console.log('copied to clipboard');
            }).catch((err) => {
          console.log('Error copying  objects', err);
        });
      } catch (err) {
        console.error('Error copying objects for clipboard: ', err);
      }
      if (action === 'cut') {
        this.deleteCopiedLayers(tempArray);
      }
    },
    setTargetEmpty() {
      this.targets = [];
    },
    onCanvasDoubleClick(e) {
      const groupChild = this.findGroupChildAtEvent(e);
      if (groupChild) {
        const isAlreadySelected =
            this.activeObject && this.activeObject.Id === groupChild.Id;

        if (isAlreadySelected && groupChild.type === 'text' && !groupChild.locked) {
          this.setupActiveEditor();
          return;
        }

        this.selectGroupChildById(groupChild.Id);
        return;
      }

      if (this.activeObject && this.activeObject.type === 'text') {
        this.setupActiveEditor();
      }
      if (this.activeObject && this.activeObject.type === 'table') {
        EventBus.$emit('tableEnterEditMode', this.activeObject.Id);
      }
    },

    onCanvasClick(e) {
      e.stopPropagation();
      const editingEl = document.querySelector('.focus-text-editor, .mce-edit-focus, .fmce-edit-focus');
      if (editingEl && !editingEl.contains(e.target)) {
        this.disableTinymce();
      }
    },
    setElementAnimation(payload) {
      if (!this.activeObject) {
        return;
      }

      const animationStyle = payload.animation && payload.animation.style
          ? payload.animation.style
          : '';
      const isGroupTarget = this.activeObject.type === 'group';
      const candidates = getPropertyApplyTargets(
          this.objectList,
          this.selectedObjectsList,
          this.activeObject,
      );
      const primary = (payload.primaryObjectId
              && findObjectById(this.objectList, payload.primaryObjectId))
          || (this.activeObject.type !== 'group' ? this.activeObject : null)
          || candidates[0];
      let targets = filterSameTypeAnimationTargets(candidates, primary, animationStyle);

      if (!targets.length && primary && primary.type !== 'group') {
        targets = [primary];
      }
      if (!targets.length) {
        return;
      }

      if (isGroupTarget) {
        this.$set(this.activeObject, 'animation', {
          style: '', speed: 50, onEnter: false, onExit: false, distance: 50,
        });
        this.$set(this.activeObject, 'isAnimated', false);
      }

      const animation = {
        style: animationStyle,
        speed: payload.animation.speed ? payload.animation.speed : 50,
        onEnter: payload.animation.onEnter ? payload.animation.onEnter : false,
        onExit: payload.animation.onExit ? payload.animation.onExit : false,
        distance: payload.animation.distance ? payload.animation.distance : 50,
      };

      targets.forEach((target) => {
        this.$set(target, 'animation', {...animation});
        this.$set(target, 'isAnimated', payload.isAnimated);
      });

      const hideSelectionChrome = isGroupTarget || targets.length > 1;
      if (!animation.style && payload.isAnimated === false) {
        if (hideSelectionChrome) {
          this.restoreBoundingBoxAfterAnimation();
        }
        EventBus.$emit(ApplicationEventTypes.LayerPanelEvents.UPDATE_DATA_ON_LAYER_SWAP, this.objectList);
        EventBus.$emit(ApplicationEventTypes.Product.SYNC_VIDEO_MODE_AFTER_ELEMENT_ANIMATION);
      }
    },

    hideBoundingBox(hide = true) {
      if (hide) {
        if (this.moveableObj) {
          this.moveableObj.target = null;
          if (typeof this.moveableObj.updateTarget === 'function') {
            this.moveableObj.updateTarget();
          }
        }
        const el = document.querySelector('.rCS1w3zcxh');
        if (el) {
          el.classList.add('hide-bounding-box');
        }
      } else {
        const el = document.querySelector('.rCS1w3zcxh');
        if (el) {
          el.classList.remove('hide-bounding-box');
        }
      }
    },
    restoreBoundingBoxAfterAnimation() {
      this.hideBoundingBox(false);
      this.refreshMoveableTargets();
    },
  },
};

</script>

<style lang="scss">
.selection-icons {
  z-index: 9999999;

  .bm-small-btns {
    box-shadow: 0px 10px 20px rgba(0, 0, 0, 0.04), 0px 2px 6px rgba(0, 0, 0, 0.04), 0px 0px 1px rgba(0, 0, 0, 0.04);
  }
}

.rCS1w3zcxh {
  .moveable-line {
    background: transparent !important;
    border: 1px dashed #3BB0FA !important;
    //left: -5px !important ;
  }

  .moveable-area {
    &.moveable-pass {
      pointer-events: var(--moveable-pointer-events) !important;
    }
  }

  .moveable-control {
    background: $white !important;
    border: 2px solid $blue50 !important;

    &.moveable-e {
      height: 60px;
      width: 8px;
      border-radius: 4px;
      left: 3px !important;
      top: -23px;
      opacity: var(--moveable-ew-opacity);
      z-index: 9;
    }

    &.moveable-n {
      height: 8px;
      width: 60px;
      border-radius: 4px;
      left: -23px;
      top: 3px;
      opacity: var(--moveable-ns-opacity);
      z-index: 9;
    }

    &.moveable-w {
      height: 60px;
      width: 8px;
      border-radius: 4px;
      left: 3px;
      top: -23px;
      opacity: var(--moveable-ew-opacity);
      z-index: 9;
    }

    &.moveable-s {
      height: 8px;
      width: 60px;
      border-radius: 4px;
      left: -23px;
      top: 3px;
      opacity: var(--moveable-ns-opacity);
      z-index: 9;
    }

    .moveable-line {
      height: 1.5px !important;

    }

    .moveable-rotation-line {
      height: 100% !important;
      width: 1.5px !important;
    }
  }
}

.canvas-main-html-container {
  svg {
    width: auto !important;
    height: auto !important;
  }
}

.moveable-dragging {
  cursor: url('../../assets/images/active-selction.png'), auto !important;
}

.extended-button {
  width: 36px;
  height: 36px;
  padding: 0px;
  background-color: white;
  border-radius: 5px;
  border: 1px solid #ededed;
  cursor: pointer !important;
  font-family: fontAwesome;
  font-size: 14px;
  color: #7a7777;
  box-shadow: 4px 3px 7px 0px #808080a6;

  &.delete-button {
    &:before {
      content: "\F1F8";
    }
  }

  &.duplicate-button {
    &:before {
      content: "\f24d";
    }
  }

  &.drag-button {
    &:before {
      content: "\f047";
    }
  }

  &.menu-button {
    &:before {
      content: "\f141";
    }
  }

  &.crop-button {
    &:before {
      content: "\f125";
    }
  }

  &.text-ai-button {
    &:before {
      content: "\f0d0";
      font-size: 14px;
    }
  }
}

.dimension-viewer {
  background: $blue50;
  border: none;
  color: white;
  font-family: monospace;
  font-size: 12px;
  white-space: nowrap;
  border-radius: 2px;
  padding: 2px 5px;
  line-height: 11px;
}

</style>
